import React from 'react';
import { Box, ButtonBase, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

// One reason code (Figma "Reason pill")
export const ReasonPill = ({ label, selected, onClick, disabled }) => (
    <ButtonBase
        role="radio"
        aria-checked={selected}
        onClick={onClick}
        disabled={disabled}
        sx={{
            px: 1.75,
            py: 0.75,
            borderRadius: 999,
            border: '1px solid',
            borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'background.paper',
            color: selected ? 'primary.main' : 'text.primary',
            fontSize: '0.8125rem',
            fontWeight: selected ? 600 : 500,
            transition: (theme) => theme.transitions.create(['background-color', 'border-color', 'color'], {
                duration: theme.transitions.duration.shortest,
            }),
            '&:hover': { borderColor: 'primary.main' },
            '&.Mui-focusVisible': { boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}` },
        }}
    >
        {label}
    </ButtonBase>
);

// After the reveal, one per dimension whose codes differ (Figma "Reason-code picker"): pick one
// reason from the run's reason-code set (codebook/adjudication.json). The picked reason shows its
// definition, so adjudicators apply the same meaning. Never shown in tie mode or when codes agree.
const ReasonCodePicker = ({ dimensionLine, reasons, value, onChange, disabled = false, id }) => {
    const picked = reasons.find((r) => r.code === value);
    return (
        <Paper component="fieldset" sx={{ m: 0, p: 2, minWidth: 0 }}>
            <Typography component="legend" variant="overline" sx={{ color: 'text.secondary', p: 0, float: 'left', width: '100%' }}>
                Why do the codes differ?
            </Typography>
            <Typography id={id} variant="subtitle2" sx={{ clear: 'both', mb: 1.25 }}>
                {dimensionLine}
            </Typography>
            <Box role="radiogroup" aria-labelledby={id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {reasons.map((reason) => (
                    <ReasonPill
                        key={reason.code}
                        label={reason.label}
                        selected={value === reason.code}
                        disabled={disabled}
                        onClick={() => onChange(reason.code)}
                    />
                ))}
            </Box>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.25, minHeight: '1.45em' }} aria-live="polite">
                {picked ? picked.definition : 'Choose one reason.'}
            </Typography>
        </Paper>
    );
};

export default ReasonCodePicker;
